import { wallClockInZone } from "../data/wall-clock.js";
import { resolveMenuFormat } from "../dsl/template.js";
import { maxFittingItems } from "./fit-items.js";
/** True when this leaf already paints a later category via inline `sectionHeading`. */
export function isMixedCategoryPlan(plan) {
    return plan.items.some((item) => {
        const heading = item.sectionHeading;
        return typeof heading === 'string' && heading.trim().length > 0;
    });
}
/**
 * Items per page at the reference density.
 *
 * Blueprints declare a capacity, but that assumes typical content; a category
 * of photo-heavy dishes with three-line descriptions needs the same layout to
 * hold fewer of them. This is the multiplier that reconciles the two.
 */
const REFERENCE_DENSITY = 2.0;
export function paginate(menu, profile, template, prng, now, 
/** Brand-resolved tokens — must match reflow/render or pages under/over-pack. */
tokens = template.tokens) {
    const diagnostics = [];
    const flow = template.flow;
    const plans = [];
    if (flow.includeCover) {
        plans.push({ group: 'cover', items: [], pageInCategory: 0, pagesInCategory: 1 });
    }
    const localNow = wallClockInZone(now, menu.restaurant.timezone);
    const categories = [...menu.categories]
        .sort((a, b) => a.position - b.position)
        .map((category) => ({
        category,
        items: availableItems(category, localNow),
    }))
        .filter(({ category, items }) => {
        if (!withinWindow(category, localNow)) {
            diagnostics.push({
                level: 'info',
                code: 'empty-category',
                message: `Category "${category.name}" is outside its availability window and was skipped`,
            });
            return false;
        }
        if (items.length > 0)
            return true;
        diagnostics.push({
            level: 'info',
            code: 'empty-category',
            message: `Category "${category.name}" has no available items and was skipped`,
        });
        return false;
    });
    // The table of contents needs page numbers, so it is reserved here and its
    // entries are filled in once the whole book is laid out.
    const tocPlaceholder = flow.includeTableOfContents && categories.length > 2;
    if (tocPlaceholder) {
        plans.push({
            group: 'toc',
            items: [],
            pageInCategory: 0,
            pagesInCategory: 1,
            title: 'Contents',
        });
    }
    const spotlightBudget = flow.allowSpotlights ? flow.maxSpotlights : 0;
    const spotlightCandidates = pickSpotlightCandidates(categories, spotlightBudget, prng);
    const spotlightsByCategory = new Map();
    for (const candidate of spotlightCandidates) {
        const bucket = spotlightsByCategory.get(candidate.categoryId) ?? [];
        bucket.push(candidate.item);
        spotlightsByCategory.set(candidate.categoryId, bucket);
    }
    for (const { category, items } of categories) {
        const categoryProfile = profile.categories[category.id];
        const density = categoryProfile?.density ?? REFERENCE_DENSITY;
        const wantsDivider = flow.dividers === 'perCategory' ||
            (flow.dividers === 'auto' && (category.startsNewSection || items.length >= 6));
        if (wantsDivider && hasBlueprint(template, 'divider')) {
            plans.push({
                group: 'divider',
                category,
                items: [],
                pageInCategory: 0,
                pagesInCategory: 1,
                title: category.name,
            });
        }
        const perPage = itemsPerPage(template, items.length, density, tokens);
        const chunks = chunkWithOrphanControl(items, perPage, flow.minOrphanItems);
        chunks.forEach((chunk, index) => {
            plans.push({
                group: 'category',
                category,
                items: chunk,
                pageInCategory: index,
                pagesInCategory: chunks.length,
                title: index === 0 ? category.name : undefined,
            });
        });
        for (const item of spotlightsByCategory.get(category.id) ?? []) {
            plans.push({
                group: 'spotlight',
                category,
                items: [],
                spotlightItem: item,
                pageInCategory: 0,
                pagesInCategory: 1,
            });
        }
    }
    if (flow.includeInfoPage && hasBlueprint(template, 'info') && restaurantHasInfoContent(menu)) {
        plans.push({
            group: 'info',
            items: [],
            pageInCategory: 0,
            pagesInCategory: 1,
            title: 'Find us',
        });
    }
    if (flow.includeBackCover && hasBlueprint(template, 'backCover')) {
        plans.push({ group: 'backCover', items: [], pageInCategory: 0, pagesInCategory: 1 });
    }
    // A spread with a blank right-hand side reads as a printing error, so odd
    // books get a quiet filler page before the back cover.
    balanceSpreadPlans(plans, template, flow, diagnostics);
    return { plans, diagnostics };
}
/** Insert a filler so spread books never end on an unpaired left page. */
export function balanceSpreadPlans(plans, template, flow = template.flow, diagnostics = []) {
    if (!(template.canvas.spread && flow.balanceSpreads))
        return plans;
    if (plans.length % 2 === 0)
        return plans;
    if (hasBlueprint(template, 'filler')) {
        const insertAt = flow.includeBackCover && plans[plans.length - 1]?.group === 'backCover'
            ? plans.length - 1
            : plans.length;
        plans.splice(insertAt, 0, {
            group: 'filler',
            items: [],
            pageInCategory: 0,
            pagesInCategory: 1,
        });
    }
    else {
        diagnostics.push({
            level: 'info',
            code: 'orphan-page',
            message: 'Book has an odd page count and the template has no filler page',
        });
    }
    return plans;
}
/**
 * Format-aware pagination entry point.
 *
 * `digital_book` uses the full book flow; flat formats pack all categories into
 * one or two pages without TOC or spread balancing.
 */
export function paginateForFormat(menu, profile, template, prng, now, tokens = template.tokens) {
    const format = resolveMenuFormat(template);
    switch (format) {
        case 'single_page':
        case 'flyer':
        case 'wall_board':
        case 'tv_slide':
        case 'interior_sheet':
        case 'pocket_menu':
        case 'social_post':
            return paginateFlat(menu, template, now);
        case 'table_tent':
            return paginateTableTent(menu, template, now);
        case 'digital_book':
        default:
            return paginate(menu, profile, template, prng, now, tokens);
    }
}
/** Delivery / takeout: compact layout without book chrome. */
function paginateFlat(menu, template, now) {
    const diagnostics = [];
    const categories = collectCategories(menu, now, diagnostics);
    const flow = template.flow;
    const plans = [];
    if (flow.includeCover && hasBlueprint(template, 'cover')) {
        plans.push({
            group: 'cover',
            items: [],
            pageInCategory: 0,
            pagesInCategory: 1,
            title: menu.title,
        });
    }
    if (categories.length === 0) {
        if (plans.length === 0) {
            plans.push({ group: 'category', items: [], pageInCategory: 0, pagesInCategory: 1 });
        }
        return { plans, diagnostics };
    }
    for (const { category, items } of categories) {
        const perPage = flatItemsPerPage(template, items.length);
        const chunks = [];
        for (let index = 0; index < items.length; index += perPage) {
            chunks.push(items.slice(index, index + perPage));
        }
        for (let index = 0; index < chunks.length; index += 1) {
            plans.push({
                group: 'category',
                category,
                items: chunks[index],
                pageInCategory: index,
                pagesInCategory: chunks.length,
                title: index === 0 ? category.name : undefined,
            });
        }
    }
    if (flow.includeBackCover && hasBlueprint(template, 'backCover')) {
        plans.push({
            group: 'backCover',
            items: [],
            pageInCategory: 0,
            pagesInCategory: 1,
        });
    }
    return { plans, diagnostics };
}
/** Table tent: front panel (title) + back panel (menu items). */
function paginateTableTent(menu, template, now) {
    const diagnostics = [];
    const categories = collectCategories(menu, now, diagnostics);
    const allItems = categories.flatMap(({ items }) => items);
    const plans = [];
    if (template.flow.includeCover && hasBlueprint(template, 'cover')) {
        plans.push({
            group: 'cover',
            items: [],
            pageInCategory: 0,
            pagesInCategory: 2,
            title: menu.title,
        });
    }
    plans.push({
        group: 'category',
        category: categories[0]?.category,
        items: allItems.slice(0, flatItemsPerPage(template, allItems.length)),
        pageInCategory: 0,
        pagesInCategory: 1,
        title: categories.length > 1 ? categories.map(({ category }) => category.name).join(' · ') : categories[0]?.category.name,
    });
    if (template.flow.includeBackCover) {
        diagnostics.push({
            level: 'info',
            code: 'table-tent-flow',
            message: 'Table-tent uses its second panel for menu content; back-cover and info-page rules are not applicable',
        });
    }
    return { plans, diagnostics };
}
function collectCategories(menu, now, diagnostics) {
    const localNow = wallClockInZone(now, menu.restaurant.timezone);
    return [...menu.categories]
        .sort((a, b) => a.position - b.position)
        .map((category) => ({
        category,
        items: availableItems(category, localNow),
    }))
        .filter(({ category, items }) => {
        if (!withinWindow(category, localNow)) {
            diagnostics.push({
                level: 'info',
                code: 'empty-category',
                message: `Category "${category.name}" is outside its availability window and was skipped`,
            });
            return false;
        }
        if (items.length > 0)
            return true;
        diagnostics.push({
            level: 'info',
            code: 'empty-category',
            message: `Category "${category.name}" has no available items and was skipped`,
        });
        return false;
    });
}
function flatItemsPerPage(template, totalItems) {
    const candidates = template.blueprints.filter((blueprint) => blueprint.group === 'category' && Boolean(blueprint.capacity));
    if (candidates.length === 0) {
        return Math.max(1, Math.min(totalItems, flatFormatLimit(template)));
    }
    const max = Math.max(...candidates.map((blueprint) => blueprint.capacity.max));
    return Math.max(1, Math.min(totalItems, Math.min(max * 3, flatFormatLimit(template))));
}
function flatFormatLimit(template) {
    switch (resolveMenuFormat(template)) {
        case 'tv_slide':
            return 6;
        case 'social_post':
            return 4;
        case 'pocket_menu':
            return 12;
        case 'interior_sheet':
            return 24;
        default:
            return Number.MAX_SAFE_INTEGER;
    }
}
function availableItems(category, now) {
    return [...category.items]
        .filter((item) => item.available)
        .filter((item) => withinWindow(item, now))
        .sort((a, b) => a.position - b.position);
}
/**
 * Time-of-day availability.
 *
 * Breakfast items disappearing from the afternoon menu is a real requirement,
 * and doing it at layout time means the guest's cached page never advertises
 * something the kitchen has stopped making.
 */
export function withinWindow(item, now) {
    const availability = item.availability;
    if (!availability)
        return true;
    if (availability.daysOfWeek?.length && !availability.daysOfWeek.includes(now.getDay())) {
        return false;
    }
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    const start = parseMinutes(availability.startTime);
    const end = parseMinutes(availability.endTime);
    if (start === null || end === null)
        return true;
    // A window that wraps past midnight (22:00-02:00) is inclusive of both ends
    // of the day rather than empty.
    return start <= end
        ? minutesNow >= start && minutesNow <= end
        : minutesNow >= start || minutesNow <= end;
}
function parseMinutes(value) {
    if (!value)
        return null;
    const match = /^(\d{1,2}):(\d{2})$/.exec(value);
    if (!match)
        return null;
    return Number(match[1]) * 60 + Number(match[2]);
}
function hasBlueprint(template, group) {
    return template.blueprints.some((blueprint) => blueprint.group === group);
}
/** Skip a Find-us leaf when the restaurant has nothing to print there. */
function restaurantHasInfoContent(menu) {
    const restaurant = menu.restaurant;
    return Boolean(restaurant.address ||
        restaurant.phone ||
        restaurant.website ||
        restaurant.email ||
        restaurant.wifiName ||
        menu.footnote);
}
function itemsPerPage(template, totalItems, density, tokens) {
    const candidates = template.blueprints.filter((blueprint) => blueprint.group === 'category' && Boolean(blueprint.capacity));
    if (candidates.length === 0)
        return Math.max(1, Math.min(totalItems, 8));
    // Weight toward layouts the selector actually prefers (high weight), not a
    // flat average that lets a 2-item photo grid drag a 12-item list down to ~4.
    let weightSum = 0;
    let weightedFit = 0;
    let geometricCeiling = 1;
    const fitScope = {
        category: { name: '', description: '', items: [], itemCount: totalItems },
        page: { items: [], itemCount: totalItems, isFirstOfCategory: true },
    };
    for (const blueprint of candidates) {
        const fit = maxFittingItems(blueprint, template.canvas, tokens, 48, fitScope);
        const weight = Math.max(1, blueprint.weight);
        weightSum += weight;
        weightedFit += fit * weight;
        geometricCeiling = Math.max(geometricCeiling, fit);
    }
    const geometricTarget = weightedFit / Math.max(1, weightSum);
    const authorCeiling = Math.max(...candidates.map((blueprint) => blueprint.capacity.max));
    const authorFloor = Math.min(...candidates.map((blueprint) => blueprint.capacity.min));
    // Prefer packing near the geometric target — preferred was systematically
    // under-filling pages and leaving half-empty leaves next to continuations.
    const authorPreferred = Math.max(...candidates.map((blueprint) => blueprint.capacity.preferred));
    const densityFactor = Math.min(1, REFERENCE_DENSITY / Math.max(density, 0.5));
    const softDensity = Math.max(0.9, densityFactor);
    const target = Math.round(Math.max(geometricTarget, authorPreferred * 0.85) * softDensity);
    const ceiling = Math.min(authorCeiling, geometricCeiling);
    const floor = Math.min(authorFloor, ceiling);
    return Math.max(1, Math.min(Math.max(floor, target), ceiling, totalItems));
}
/**
 * Greedy split, then rebalance so the last page is not nearly empty.
 *
 * Nine items at eight per page would otherwise leave one dish alone on a page,
 * which looks like a mistake; spreading them 5/4 does not.
 */
function chunkWithOrphanControl(items, perPage, minOrphan) {
    if (items.length === 0)
        return [];
    if (items.length <= perPage)
        return [items];
    const pageCount = Math.ceil(items.length / perPage);
    const remainder = items.length - (pageCount - 1) * perPage;
    if (remainder >= Math.max(minOrphan, 1)) {
        const chunks = [];
        for (let index = 0; index < items.length; index += perPage) {
            chunks.push(items.slice(index, index + perPage));
        }
        return chunks;
    }
    // Even distribution: the first `extra` pages take one item more.
    const base = Math.floor(items.length / pageCount);
    const extra = items.length % pageCount;
    const chunks = [];
    let cursor = 0;
    for (let page = 0; page < pageCount; page += 1) {
        const size = base + (page < extra ? 1 : 0);
        chunks.push(items.slice(cursor, cursor + size));
        cursor += size;
    }
    return chunks;
}
/**
 * Chooses which dishes earn a full-page spotlight.
 *
 * Featured items with a photo come first; the seeded shuffle decides between
 * equally good candidates so two restaurants with similar menus do not get the
 * same pages highlighted.
 */
function pickSpotlightCandidates(categories, budget, prng) {
    if (budget <= 0)
        return [];
    const pool = categories.flatMap(({ category, items }) => items
        .filter((item) => item.image && item.featured)
        .map((item) => ({ categoryId: category.id, item })));
    if (pool.length === 0)
        return [];
    const shuffled = prng.fork('spotlights').shuffle(pool);
    // At most one per category, so a single photogenic section cannot take over
    // the whole book.
    const seen = new Set();
    const chosen = [];
    for (const candidate of shuffled) {
        if (chosen.length >= budget)
            break;
        if (seen.has(candidate.categoryId))
            continue;
        seen.add(candidate.categoryId);
        chosen.push(candidate);
    }
    return chosen;
}
//# sourceMappingURL=paginate.js.map